import SimpleGrid from "@ui-kit/layout/Grid/SimpleGrid.web";
import { NumericFormatProps } from "@shared/components/ui-kit/form/NumericFormat/NumericFormat.web";
import Icon from "@shared/components/ui-kit/icons/Icon.web";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import Tooltip from "@shared/components/ui-kit/overlays/Tooltip/Tooltip.web";
import Text from "@shared/components/ui-kit/typography/Text/Text.web";
import { enums, enumsTypes } from "@shared/constants";
import { AnalyticsService } from "@shared/services/analytics";
import { useTranslation } from "@shared/utils/internationalization";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import Button from "@ui-kit/form/Button/Button.web";
import { RefObject } from "react";
import { DONATION_AMOUNTS_TO_SHOW, DonationAmountType } from "../constants";

interface DonationsInputProps {
  isBasketUpdating: boolean;
  isLoading: boolean;
  isError: boolean;
  amountSelectedDisplay: string;
  onAmountSelect: (amountKey: string) => void;
  customDonationAmt: NumericFormatProps;
  onCustomAmountChange: (value: string) => void;
  customDonationInputRef: RefObject<HTMLInputElement>;
  charityCurrency: enumsTypes.CurrencyMapIdType;
  customRequestDonation: () => void;
  defaultDonationAmount: string;
}
export default function DonationsInput({
  isBasketUpdating,
  isLoading,
  isError,
  amountSelectedDisplay,
  onAmountSelect,
  customDonationAmt,
  onCustomAmountChange,
  customDonationInputRef,
  charityCurrency,
  customRequestDonation,
  defaultDonationAmount,
}: DonationsInputProps) {
  const { t } = useTranslation("common");

  const donationAmountsToShow = enums.DONATION_AMOUNT_MAP_BY_LIST.filter(
    (donAmount) =>
      DONATION_AMOUNTS_TO_SHOW.includes(donAmount.id as DonationAmountType) ||
      donAmount.id === defaultDonationAmount
  );

  return (
    <Box mt={4}>
      <Text as="p" fontWeight="bold" size="sm">
        {t("Select donation amount")}
      </Text>
      <Box mt="2xs">
        {/* Add 1 to the number of columns to account for the custom amount input */}
        <SimpleGrid
          columns={{
            base: Math.ceil((donationAmountsToShow.length + 1) / 2), // Mobile: 2 rows
            md: donationAmountsToShow.length + 1, // Desktop: 1 row
          }}
          spacing={2}
        >
          {donationAmountsToShow.map((donAmount) => {
            return (
              <Button
                data-testid={`donation-amount-button-${donAmount.id}`}
                key={donAmount.id}
                isDisabled={isBasketUpdating || isLoading || isError}
                isActive={
                  !isBasketUpdating &&
                  !isLoading &&
                  !isError &&
                  amountSelectedDisplay === donAmount.key
                }
                onClick={() => {
                  AnalyticsService.buttonClick({
                    label: "donation",
                    custom_params: {
                      datum: "selection",
                      type: "preset",
                      amount: donAmount.amount,
                      suggested: Boolean(
                        donAmount.id === defaultDonationAmount
                      ),
                    },
                  });

                  if (amountSelectedDisplay !== donAmount.key) {
                    onAmountSelect(donAmount.key);
                  }
                }}
                variant={
                  !isBasketUpdating &&
                  !isLoading &&
                  !isError &&
                  amountSelectedDisplay === donAmount.key
                    ? "solid"
                    : "outline"
                }
                size="sm"
              >
                {donAmount.id === defaultDonationAmount && (
                  <>
                    <Tooltip
                      placement="top"
                      hasArrow
                      label={<Text>{t("Suggested donation")} </Text>}
                      usePortal={false}
                      shouldWrapChildren
                    >
                      <Icon.BasicStar
                        w="element.md"
                        h="element.md"
                        mr="2xs"
                        mb="none"
                      />
                    </Tooltip>
                  </>
                )}

                <CurrencyFormat
                  value={donAmount.amount}
                  currency={charityCurrency}
                  decimalScale={0}
                  displayType="text"
                />
              </Button>
            );
          })}
          {/* Custom amount input */}
          <CurrencyFormat
            value={customDonationAmt?.value ?? amountSelectedDisplay}
            style={{
              borderRadius: "5px",
              border: "1px solid",
              borderColor: "var(--chakra-colors-primary-solid)",
              textAlign: "center",
            }}
            onValueChange={(valueObj: { value: string | null }) => {
              onCustomAmountChange(valueObj.value ?? "");
            }}
            isAllowed={(values) => {
              const { floatValue } = values;
              return floatValue != null ? floatValue <= 9999.99 : true;
            }}
            valueIsNumericString
            currency={charityCurrency}
            onBlur={() => {
              customRequestDonation();
            }}
            isDisabled={isBasketUpdating || isLoading || isError}
            inputRef={customDonationInputRef}
            placeholder="0"
          />
        </SimpleGrid>
      </Box>
      <Text as="p" size="sm" color="gray.600" mt="xs">
        {t("All prices are in ") + charityCurrency.toUpperCase()}
      </Text>
    </Box>
  );
}
